/**
 * Modal création / édition d'une réservation (staff Sauf Imprévu).
 * Cloné sur EventEditSheet (overlay full-screen, masque la TabBar, footer sticky).
 * Pas de cover ici : un formulaire métier (nom, contact, créneau, couverts…).
 *
 * Mode : `reservation === null` → création (POST /reservations, source 'phone'),
 * sinon édition (PATCH /reservations/:id). L'admin gate est côté serveur
 * (requireAuth + is_admin) ; le JWT du staff est envoyé en Bearer.
 *
 * Indicateur capacité : interroge /disponibilites au changement de date/service
 * /couverts pour afficher les places restantes (la source de vérité reste le
 * serveur, qui renvoie 409 si dépassement).
 */
import React, { useEffect, useRef, useState } from 'react';
import './EditReservationSheet.css';
import { apiFetch } from '../../utils/api';

const NOM_MAX = 120;
const NOTES_MAX = 2000;

export type ReservationService = 'midi' | 'soir';
export type ReservationStatus = 'confirme' | 'annule' | 'termine' | 'no-show';

export interface StaffReservation {
  id: number;
  nom: string;
  telephone: string | null;
  email: string | null;
  date: string;
  heure: string;
  couverts: number;
  service: ReservationService | null;
  notes: string | null;
  statut: ReservationStatus | null;
  /** Table attribuée en salle. Posée par « Placer à table », pas par ce formulaire. */
  table_num?: string | null;
}

// Cahier beta : le statut n'est plus saisissable ici. « Confirmé » est le défaut
// (pas de confirmation manuelle) et « Annuler » passe par le burger de la card ;
// « Terminé » est déduit de l'heure prévue (utils/reservationStatus) et « No-show »
// sort de l'UI en attendant la vue « Bilan de service ». Le PATCH renvoie le statut
// existant tel quel : une édition ne change jamais l'état de la résa.

interface Props {
  /** null = mode création. */
  reservation: StaffReservation | null;
  /** Pré-remplit la date en création (jour sélectionné dans la liste). */
  defaultDate?: string;
  defaultService?: ReservationService;
  onClose: () => void;
  onSaved: () => void;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}
/** Normalise "9:00" / "20h00" → "09:00" (acceptable par <input type=time>). */
function normalizeHeure(h: string): string {
  const m = h.replace('h', ':').match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return h;
  return `${pad2(parseInt(m[1], 10))}:${m[2]}`;
}

export function EditReservationSheet({ reservation, defaultDate, defaultService, onClose, onSaved }: Props) {
  const isEdit = reservation !== null;
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState('');
  const [heure, setHeure] = useState('20:00');
  const [couverts, setCouverts] = useState(2);
  const [service, setService] = useState<ReservationService>('soir');
  const [statut, setStatut] = useState<ReservationStatus>('confirme');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [capacity, setCapacity] = useState<number | null>(null);
  const nomInputRef = useRef<HTMLInputElement | null>(null);

  // Hydrate l'état à l'ouverture.
  useEffect(() => {
    setNom(reservation?.nom ?? '');
    setTelephone(reservation?.telephone ?? '');
    setEmail(reservation?.email ?? '');
    setDate(reservation?.date ?? defaultDate ?? '');
    setHeure(reservation ? normalizeHeure(reservation.heure) : '20:00');
    setCouverts(reservation?.couverts ?? 2);
    setService(reservation?.service ?? defaultService ?? 'soir');
    setStatut(reservation?.statut ?? 'confirme');
    setNotes(reservation?.notes ?? '');
    setToast(null);
  }, [reservation?.id, defaultDate, defaultService]);

  // Masque la TabBar tant que la modale est ouverte.
  useEffect(() => {
    document.body.classList.add('modal-sheet-open');
    return () => document.body.classList.remove('modal-sheet-open');
  }, []);

  // Auto-focus le nom en création.
  useEffect(() => {
    if (!isEdit) {
      const t = setTimeout(() => nomInputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [isEdit]);

  // Indicateur capacité : interroge /disponibilites quand date/service/couverts changent.
  useEffect(() => {
    if (!date) { setRemaining(null); setCapacity(null); return; }
    let cancelled = false;
    const url = `/reservations/disponibilites?date=${date}&heure=${encodeURIComponent(heure)}&couverts=${couverts}&service=${service}`;
    apiFetch(url, { auth: false })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setRemaining(typeof d.remaining === 'number' ? d.remaining : null);
        setCapacity(typeof d.capacity === 'number' ? d.capacity : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [date, service, couverts, heure]);

  const canSave = nom.trim().length > 0 && date.length > 0 && heure.length > 0;

  const onSave = async () => {
    if (saving || !canSave) return;
    setSaving(true);
    setToast(null);
    const token = localStorage.getItem('alterrego_jwt');
    if (!token) {
      setToast({ kind: 'err', msg: 'Non connecté' });
      setSaving(false);
      return;
    }
    const headers = {
      'Content-Type': 'application/json',
    };
    try {
      let r: Response;
      if (isEdit && reservation) {
        // Édition : PATCH partiel (tous les champs éditables).
        r = await apiFetch(`/reservations/${reservation.id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({
            nom: nom.trim(),
            telephone: telephone.trim() || null,
            email: email.trim() || null,
            date,
            heure,
            couverts,
            service,
            statut,
            notes: notes.trim() || null,
          }),
        });
      } else {
        // Création staff : source 'phone' (réservation prise au téléphone / au comptoir).
        r = await apiFetch(`/reservations`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nom: nom.trim(),
            telephone: telephone.trim() || null,
            email: email.trim() || null,
            date,
            heure,
            couverts,
            service,
            notes: notes.trim() || null,
            source: 'phone',
          }),
        });
      }
      if (r.status === 409) {
        const data = await r.json().catch(() => ({}));
        setToast({ kind: 'err', msg: `Capacité dépassée (${data.remaining ?? 0} places restantes)` });
        setSaving(false);
        return;
      }
      if (!r.ok) {
        setToast({ kind: 'err', msg: 'Enregistrement échoué' });
        setSaving(false);
        return;
      }
      setToast({ kind: 'ok', msg: 'Enregistré' });
      onSaved();
      setTimeout(() => onClose(), 500);
    } catch {
      setToast({ kind: 'err', msg: 'Erreur réseau — réessaie' });
    } finally {
      setSaving(false);
    }
  };

  const overCapacity = remaining !== null && couverts > remaining;

  return (
    <div className="ers-backdrop" onClick={onClose}>
      <div className="ers-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="ers-close" onClick={onClose} aria-label="Fermer">×</button>

        <div className="ers-header">
          <h2>{isEdit ? 'Éditer la réservation' : 'Nouvelle réservation'}</h2>
          {isEdit && <p className="ers-resa-name">{reservation?.nom}</p>}
        </div>

        {/* Identité */}
        <div className="ers-section">
          <h3>Nom <span className="ers-req" aria-hidden="true">*</span></h3>
          <input
            ref={nomInputRef}
            className="ers-input"
            value={nom}
            onChange={(e) => setNom(e.target.value.slice(0, NOM_MAX))}
            placeholder="Nom du client"
          />
        </div>

        <div className="ers-section">
          <div className="ers-grid2">
            <label className="ers-field">
              <span>Téléphone</span>
              <input className="ers-input" type="tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="06…" />
            </label>
            <label className="ers-field">
              <span>Email</span>
              <input className="ers-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@…" />
            </label>
          </div>
        </div>

        {/* Créneau */}
        <div className="ers-section">
          <h3>Créneau</h3>
          <div className="ers-chips">
            {(['midi', 'soir'] as ReservationService[]).map((s) => (
              <button
                key={s}
                type="button"
                className={`ers-chip${service === s ? ' active' : ''}`}
                onClick={() => setService(s)}
              >
                {s === 'midi' ? 'Déjeuner' : 'Dîner'}
              </button>
            ))}
          </div>
          <div className="ers-grid2" style={{ marginTop: 8 }}>
            <label className="ers-field">
              <span>Date <span className="ers-req" aria-hidden="true">*</span></span>
              <input className="ers-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="ers-field">
              <span>Heure <span className="ers-req" aria-hidden="true">*</span></span>
              <input className="ers-input" type="time" value={heure} onChange={(e) => setHeure(e.target.value)} />
            </label>
          </div>
        </div>

        {/* Couverts + capacité */}
        <div className="ers-section">
          <h3>Couverts</h3>
          <div className="ers-stepper">
            <button type="button" onClick={() => setCouverts((n) => Math.max(1, n - 1))} aria-label="Moins">−</button>
            <span className="ers-stepper-val">{couverts}</span>
            <button type="button" onClick={() => setCouverts((n) => Math.min(200, n + 1))} aria-label="Plus">+</button>
          </div>
          {remaining !== null && capacity !== null && (
            <p className={`ers-capacity${overCapacity ? ' over' : ''}`}>
              {overCapacity
                ? `⚠ ${remaining} place${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''} sur ce service`
                : `${remaining} / ${capacity} couverts disponibles (${service === 'midi' ? 'déjeuner' : 'dîner'})`}
            </p>
          )}
        </div>

        {/* Notes */}
        <div className="ers-section">
          <h3>Notes</h3>
          <textarea
            className="ers-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
            placeholder="Allergies, table, occasion…"
            rows={3}
          />
        </div>

        {toast && <div className={`ers-toast ${toast.kind}`}>{toast.msg}</div>}

        <div className="ers-footer">
          {!canSave && <p className="ers-save-hint">Renseigne au moins un nom, une date et une heure</p>}
          <div className="ers-actions">
            <button type="button" className="ers-btn ers-btn-secondary" onClick={onClose} disabled={saving}>
              Annuler
            </button>
            <button type="button" className="ers-btn ers-btn-primary" onClick={onSave} disabled={saving || !canSave}>
              {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
